// SEQ-199: the Grid section of the widget settings drawer. Draws the controls;
// every change goes through gridEditorState.ts, which a deno test proves.
//
// What an editor can set (Josh, SEQ-199, 6 Oct): the measures, the summary
// columns, the Pareto cut-offs and their labels, Top N, and the conditional
// formatting bands (thresholds, labels AND colours) per measure, so the same
// grid serves other measures and dashboards.
import { useState } from "react";
import { Plus, RotateCcw, Trash2 } from "lucide-react";
import { cutoffError, labelError, topNError } from "../../features/pnlAnalytics/gridFieldErrors";
import {
  GRID_MAX_MEASURES,
  GRID_MAX_RULES,
  GRID_MEASURES,
  GRID_SUMMARY_COLUMNS,
  gridMeasure,
  resolveGridConfig,
  type PnlGridConfig,
} from "../../lib/pnl/pnlGridConfig";
import { BAND_COLOURS, BAND_COLOUR_NAMES, badBandFields, bandsProblem, isBandColour } from "../../features/pnlAnalytics/transforms/bands";
import {
  addFormatBand,
  addFormatRule,
  addParetoBand,
  explicitGridConfig,
  removeFormatBand,
  removeFormatRule,
  removeParetoBand,
  resetFormatRules,
  setGridTopN,
  toggleGridMeasure,
  toggleGridSummary,
  updateFormatBand,
  updateParetoBand,
} from "../../features/pnlAnalytics/gridEditorState";

export interface PnlGridSettingsProps {
  grid: PnlGridConfig | undefined;
  onChange: (grid: PnlGridConfig) => void;
}

export function PnlGridSettings({ grid, onChange }: PnlGridSettingsProps) {
  const r = resolveGridConfig(grid);
  const [topNRaw, setTopNRaw] = useState<string>(r.topN === null ? "" : String(r.topN));
  const topNErr = topNError(topNRaw);
  // The typed text, even when blank: resolveGridConfig would swap a blank for the default and hide the problem.
  const restLabelText = grid?.paretoRestLabel ?? r.paretoRestLabel;
  const restLabelError = labelError(restLabelText);
  const rules = r.formatRules;
  const measuresWithoutRule = GRID_MEASURES.filter((m) => m.isPercent && !rules.some((rule) => rule.measure === m.key));

  return (
    <>
      <div className="pnl-drawer-section-heading">Grid</div>

      <div className="pnl-field">
        <span className="form-label" id="pnl-grid-measures-label">Measures for each month</span>
        <div role="group" aria-labelledby="pnl-grid-measures-label" className="pnl-grid-settings-list">
          {GRID_MEASURES.map((m) => {
            const checked = r.measures.includes(m.key);
            const disabled = !checked && r.measures.length >= GRID_MAX_MEASURES;
            return (
              <label key={m.key} className="pnl-grid-settings-check" style={{ opacity: disabled ? 0.5 : 1 }}>
                <input type="checkbox" checked={checked} disabled={disabled} onChange={() => onChange(toggleGridMeasure(grid, m.key))} />
                {m.label}
              </label>
            );
          })}
        </div>
        <p className="pnl-field-hint">Pick 1 to {GRID_MAX_MEASURES}. Each one becomes a column under every month.</p>
      </div>

      <div className="pnl-field">
        <span className="form-label" id="pnl-grid-summary-label">Summary columns</span>
        <div role="group" aria-labelledby="pnl-grid-summary-label" className="pnl-grid-settings-list">
          {GRID_SUMMARY_COLUMNS.map((c) => (
            <label key={c.key} className="pnl-grid-settings-check">
              <input type="checkbox" checked={r.summary.includes(c.key)} onChange={() => onChange(toggleGridSummary(grid, c.key))} />
              {c.label}
            </label>
          ))}
        </div>
        <label className="form-label" htmlFor="pnl-grid-summary-measure" style={{ marginTop: 8 }}>
          Rank, share and category work on
        </label>
        <select
          id="pnl-grid-summary-measure"
          className="form-input"
          value={r.summaryMeasure}
          onChange={(e) => onChange({ ...explicitGridConfig(grid), summaryMeasure: e.target.value })}
        >
          {GRID_MEASURES.filter((m) => !m.isPercent).map((m) => (
            <option key={m.key} value={m.key}>
              {m.label}
            </option>
          ))}
        </select>
      </div>

      <div className="pnl-field">
        <span className="form-label" id="pnl-grid-pareto-label">Category cut-offs</span>
        <div role="group" aria-labelledby="pnl-grid-pareto-label" className="pnl-grid-settings-rows">
          {r.paretoBands.map((band, index) => {
            const cutErr = cutoffError(band.upToPercent);
            const labErr = labelError(band.label);
            return (
              <div key={index} className="pnl-grid-settings-field">
                <div className="pnl-grid-settings-row">
                  <label className="pnl-grid-settings-inline">
                    Up to
                    <input
                      data-seq-private=""
                      type="number"
                      className="form-input"
                      step={1}
                      value={Number.isFinite(band.upToPercent) ? band.upToPercent : ""}
                      aria-label={`Cut-off ${index + 1}, percent of the total`}
                      aria-invalid={cutErr !== null}
                      aria-describedby={cutErr ? `pnl-grid-cut-err-${index}` : undefined}
                      onChange={(e) => onChange(updateParetoBand(grid, index, { upToPercent: e.target.value === "" ? Number.NaN : Number(e.target.value) }))}
                    />
                    %
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    value={band.label}
                    aria-label={`Label for cut-off ${index + 1}`}
                    aria-invalid={labErr !== null}
                    aria-describedby={labErr ? `pnl-grid-lab-err-${index}` : undefined}
                    onChange={(e) => onChange(updateParetoBand(grid, index, { label: e.target.value }))}
                  />
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={r.paretoBands.length <= 1}
                    aria-label={`Remove cut-off ${index + 1}`}
                    onClick={() => onChange(removeParetoBand(grid, index))}
                  >
                    <Trash2 size={14} aria-hidden="true" />
                  </button>
                </div>
                {cutErr && (
                  <p className="pnl-field-error" id={`pnl-grid-cut-err-${index}`} role="alert">
                    {cutErr}
                  </p>
                )}
                {labErr && (
                  <p className="pnl-field-error" id={`pnl-grid-lab-err-${index}`} role="alert">
                    {labErr}
                  </p>
                )}
              </div>
            );
          })}
          <div className="pnl-grid-settings-row">
            <label className="pnl-grid-settings-inline" style={{ flex: 1 }}>
              Everything after
              <input
                type="text"
                className="form-input"
                value={restLabelText}
                aria-label="Label for everything after the last cut-off"
                aria-invalid={restLabelError !== null}
                aria-describedby={restLabelError ? "pnl-grid-rest-err" : undefined}
                onChange={(e) => onChange({ ...explicitGridConfig(grid), paretoRestLabel: e.target.value })}
              />
            </label>
            <button type="button" className="btn btn-ghost btn-sm" disabled={r.paretoBands.length >= 5} onClick={() => onChange(addParetoBand(grid))}>
              <Plus size={14} aria-hidden="true" /> Add cut-off
            </button>
          </div>
          {restLabelError && (
            <p className="pnl-field-error" id="pnl-grid-rest-err" role="alert">
              {restLabelError}
            </p>
          )}
        </div>
        <p className="pnl-field-hint">
          The default is one cut-off at 80% labelled Vital, then Trivial. A project is placed by the share of the total held by the projects above it.
        </p>
      </div>

      <div className="pnl-field">
        <label className="form-label" htmlFor="pnl-grid-topn">
          Show the top
        </label>
        <input
          id="pnl-grid-topn"
          data-seq-private=""
          type="number"
          min={1}
          className="form-input"
          placeholder="All projects"
          value={topNRaw}
          aria-invalid={topNErr !== null}
          aria-describedby={topNErr ? "pnl-grid-topn-err" : undefined}
          onChange={(e) => {
            // What was typed stays in the box; only a valid value reaches the widget, and an invalid one is explained, never erased.
            setTopNRaw(e.target.value);
            // A valid value is applied. An invalid one is written to the draft as typed, so the draft fails validation and Save
            // stays disabled (with the note beside it) instead of quietly saving the previous value.
            onChange(topNError(e.target.value) === null ? setGridTopN(grid, e.target.value) : { ...explicitGridConfig(grid), topN: Number(e.target.value) });
          }}
        />
        {topNErr && (
          <p className="pnl-field-error" id="pnl-grid-topn-err" role="alert">
            {topNErr}
          </p>
        )}
        <p className="pnl-field-hint">The rest are added together into one Other row.</p>
        <label className="pnl-grid-settings-check" style={{ marginTop: 8 }}>
          <input type="checkbox" checked={r.averageRow} onChange={(e) => onChange({ ...explicitGridConfig(grid), averageRow: e.target.checked })} />
          Show the averages row
        </label>
        <label className="pnl-grid-settings-check">
          <input type="checkbox" checked={r.freezeFirstColumn} onChange={(e) => onChange({ ...explicitGridConfig(grid), freezeFirstColumn: e.target.checked })} />
          Keep the project name in view while scrolling sideways
        </label>
      </div>

      <div className="pnl-field">
        <span className="form-label" id="pnl-grid-format-label">Colour bands</span>
        <div role="group" aria-labelledby="pnl-grid-format-label" className="pnl-grid-settings-rows">
          {rules.map((rule) => {
            const def = gridMeasure(rule.measure);
            const problem = bandsProblem(rule.bands);
            const bad = badBandFields(rule.bands);
            return (
              <fieldset key={rule.measure} className="pnl-grid-rule">
                <legend>{def?.label ?? rule.measure}</legend>
                {rule.bands.map((band, index) => (
                  <div key={index} className="pnl-grid-settings-row">
                    {band.min === null ? (
                      <span className="pnl-grid-settings-inline pnl-grid-settings-fixed">Below the next band</span>
                    ) : (
                      <label className="pnl-grid-settings-inline">
                        From
                        <input
                          data-seq-private=""
                          type="number"
                          className="form-input"
                          key={`${rule.measure}-${index}-${band.min}`}
                          defaultValue={band.min}
                          aria-label={`${def?.label ?? rule.measure} band ${index + 1} starts at`}
                          aria-invalid={bad.min.has(index)}
                          aria-describedby={bad.min.has(index) ? `pnl-grid-rule-error-${rule.measure}` : undefined}
                          // Committed on blur, so typing a leading minus or clearing the box to retype never writes a half-typed number.
                          onBlur={(e) => {
                            const n = Number(e.target.value);
                            if (e.target.value.trim() !== "" && Number.isFinite(n) && n !== band.min) {
                              onChange(updateFormatBand(grid, rule.measure, index, { min: n }));
                            } else {
                              e.target.value = String(band.min);
                            }
                          }}
                        />
                        {def?.isPercent ? "%" : ""}
                      </label>
                    )}
                    <input
                      type="text"
                      className="form-input"
                      maxLength={40}
                      value={band.label}
                      aria-label={`${def?.label ?? rule.measure} band ${index + 1} label`}
                      aria-invalid={bad.label.has(index)}
                      aria-describedby={bad.label.has(index) ? `pnl-grid-rule-error-${rule.measure}` : undefined}
                      onChange={(e) => onChange(updateFormatBand(grid, rule.measure, index, { label: e.target.value }))}
                    />
                    <select
                      className="form-input"
                      value={band.colour}
                      aria-label={`${def?.label ?? rule.measure} band ${index + 1} colour`}
                      onChange={(e) => {
                        if (isBandColour(e.target.value)) onChange(updateFormatBand(grid, rule.measure, index, { colour: e.target.value }));
                      }}
                    >
                      {BAND_COLOURS.map((c) => (
                        <option key={c} value={c}>
                          {BAND_COLOUR_NAMES[c]}
                        </option>
                      ))}
                    </select>
                    <span className="pnl-grid-swatch" data-band-colour={band.colour} aria-hidden="true">
                      Aa
                    </span>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      disabled={rule.bands.length <= 1}
                      aria-label={`Remove ${def?.label ?? rule.measure} band ${index + 1}`}
                      onClick={() => onChange(removeFormatBand(grid, rule.measure, index))}
                    >
                      <Trash2 size={14} aria-hidden="true" />
                    </button>
                  </div>
                ))}
                {problem && (
                  <p className="pnl-field-error" id={`pnl-grid-rule-error-${rule.measure}`} role="alert">
                    {problem}
                  </p>
                )}
                <div className="pnl-grid-settings-row">
                  <button type="button" className="btn btn-ghost btn-sm" disabled={rule.bands.length >= 8} onClick={() => onChange(addFormatBand(grid, rule.measure))}>
                    <Plus size={14} aria-hidden="true" /> Add band
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange(removeFormatRule(grid, rule.measure))}>
                    <Trash2 size={14} aria-hidden="true" /> Remove colours for {def?.label ?? rule.measure}
                  </button>
                </div>
              </fieldset>
            );
          })}
          <div className="pnl-grid-settings-row">
            {rules.length < GRID_MAX_RULES && measuresWithoutRule.length > 0 && (
              <select
                className="form-input"
                value=""
                aria-label="Add colour bands for a measure"
                onChange={(e) => e.target.value && onChange(addFormatRule(grid, e.target.value))}
              >
                <option value="">Add colours for a measure</option>
                {measuresWithoutRule.map((m) => (
                  <option key={m.key} value={m.key}>
                    {m.label}
                  </option>
                ))}
              </select>
            )}
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange(resetFormatRules(grid))}>
              <RotateCcw size={14} aria-hidden="true" /> Reset to the default bands
            </button>
          </div>
        </div>
        <p className="pnl-field-hint">
          Each figure takes the colour of the band it falls in. Bands include their starting number. Colour is never the only signal: the key under the grid names every band.
          Colours on dollar columns are switched off while Safe Mode is on.
        </p>
      </div>
    </>
  );
}
